import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle,
  Star,
  Clock,
  Users,
  Award,
  Package,
  Layers,
  Settings,
  BarChart3,
} from "lucide-react";
// import colors from '../ColorConfig/colorconfig';
import GetInTouchForm from "../GetinTouchform/GetinTouch";
import Readytostart from "../Products/readytostart";
import useTheme from "../ColorConfig/usetheme";
import SEO from "../../AASeo/Seo";

const organization = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "IDLBAY Private Limited",
  alternateName: "IDLBAY",
  url: "https://idlbay.com",
  logo: "https://idlbay.com/idlbaylogo.png",
  sameAs: [
    "https://www.linkedin.com/company/idlbay/",
    "https://x.com/idlbayPvtLtd",
    "https://www.instagram.com/idlbay.official?igsh=dnIxMm50cmdxNDNy",
    "https://www.facebook.com/profile.php?id=61582746578911",
    "https://www.youtube.com/channel/UCDSb9__-mSLqeyuZYelakcw",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91-9840841840",
    contactType: "Customer Support",
    areaServed: "IN",
    availableLanguage: ["English", "Tamil"],
  },
  address: {
    "@type": "PostalAddress",
    streetAddress:
      "Office No.1, First Floor, Door No.1, B Narasimhan 3rd Street",
    addressLocality: "T-Nagar, Chennai",
    addressRegion: "Tamil Nadu",
    postalCode: "600017",
    addressCountry: "IN",
  },
};

const ProductFullDetailsView = () => {
  const colors = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [productData, setProductData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [showGetInTouch, setShowGetInTouch] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Check if device is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // In ProductFullDetailsView component
  useEffect(() => {
    if (location.state?.productData) {
      const processedProduct = {
        ...location.state.productData,
        image: location.state.productData.image
          ? location.state.productData.image.startsWith("http")
            ? location.state.productData.image
            : `https://api.dremerz.net/uploads/${location.state.productData.image}`
          : "https://via.placeholder.com/600x400/333/fff?text=Product+Image",
        // Ensure arrays are properly formatted
        keyFeatures: Array.isArray(location.state.productData.keyFeatures)
          ? location.state.productData.keyFeatures
          : [],
        benefits: Array.isArray(location.state.productData.benefits)
          ? location.state.productData.benefits
          : [],
        modules: Array.isArray(location.state.productData.modules)
          ? location.state.productData.modules
          : [],
      };
      setProductData(processedProduct);
    }
  }, [location.state, id]);

  const handleContactClick = (e) => {
    e.preventDefault();
    setShowGetInTouch(true);
    document.body.style.overflow = "hidden";
  };

  const closeGetInTouch = () => {
    setShowGetInTouch(false);
    document.body.style.overflow = "auto";
  };

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .product-details-header {
        background: linear-gradient(135deg, ${colors.bodyBlack} 0%, rgba(229, 118, 39, 0.1) 100%);
        position: relative;
        overflow: hidden;
        z-index: 1;
      }

      .product-details-header::before {
        content: '';
        position: absolute;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background: radial-gradient(circle, rgba(229, 118, 39, 0.1) 0%, transparent 70%);
        animation: rotate 20s linear infinite;
        z-index: -1;
      }

      @keyframes rotate {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      .hero-image {
        border-radius: 20px;
        overflow: hidden;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        transition: all 0.6s ease;
      }

      .hero-image:hover {
        transform: translateY(-10px);
        box-shadow: 0 30px 60px rgba(229, 118, 39, 0.2);
      }

      .hero-image img {
        width: 100%;
        height: 400px;
        object-fit: cover;
        transition: all 0.6s ease;
      }

      .hero-image:hover img {
        transform: scale(1.05);
      }

      .product-tab {
        background: transparent;
        border: 2px solid rgba(229, 118, 39, 0.3);
        color: ${colors.whiteTransparent};
        padding: 12px 24px;
        border-radius: 25px;
        margin: 0 8px 8px 0;
        transition: all 0.3s ease;
        font-weight: 500;
        cursor: pointer;
      }

      .product-tab:hover {
        border-color: var(--color-primary);
        color: ${colors.white};
        transform: translateY(-2px);
      }

      .product-tab.active {
        background: linear-gradient(135deg, var(--color-primary) 0%, ${colors.primaryHover} 100%);
        color: ${colors.white};
        border-color: var(--color-primary);
        box-shadow: 0 8px 20px rgba(229, 118, 39, 0.3);
      }

      .feature-card {
        background: linear-gradient(135deg, ${colors.cardBgBlack} 0%, rgba(229, 118, 39, 0.05) 100%);
        border: 1px solid rgba(229, 118, 39, 0.2);
        border-radius: 15px;
        padding: 1.5rem;
        margin-bottom: 1rem;
        transition: all 0.4s ease;
        position: relative;
        overflow: hidden;
      }

      .feature-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(229, 118, 39, 0.1), transparent);
        transition: left 0.6s ease;
      }

      .feature-card:hover::before {
        left: 100%;
      }

      .feature-card:hover {
        transform: translateX(10px);
        border-color: var(--color-primary);
        box-shadow: 0 15px 30px rgba(229, 118, 39, 0.2);
      }

      .tech-badge {
        background: linear-gradient(135deg, rgba(229, 118, 39, 0.2) 0%, rgba(229, 118, 39, 0.1) 100%);
        color: var(--color-primary);
        border: 1px solid rgba(229, 118, 39, 0.3);
        padding: 8px 16px;
        border-radius: 20px;
        margin: 4px;
        display: inline-block;
        font-size: 0.9rem;
        font-weight: 500;
        transition: all 0.3s ease;
      }

      .tech-badge:hover {
        background: var(--color-primary);
        color: ${colors.white};
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(229, 118, 39, 0.3);
      }

      .module-card {
        background: linear-gradient(135deg, ${colors.cardBgBlack} 0%, rgba(229, 118, 39, 0.05) 100%);
        border: 1px solid rgba(229, 118, 39, 0.2);
        border-radius: 15px;
        padding: 2rem;
        text-align: center;
        transition: all 0.4s ease;
        height: 100%;
        position: relative;
        overflow: hidden;
      }

      .module-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, rgba(229, 118, 39, 0.1) 0%, transparent 100%);
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      .module-card:hover::before {
        opacity: 1;
      }

      .module-card:hover {
        transform: translateY(-5px);
        border-color: var(--color-primary);
        box-shadow: 0 15px 30px rgba(229, 118, 39, 0.2);
      }

      .module-icon {
        width: 60px;
        height: 60px;
        background: linear-gradient(135deg, var(--color-primary) 0%, ${colors.primaryHover} 100%);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 1rem;
        transition: all 0.3s ease;
        position: relative;
        z-index: 2;
      }

      .module-card:hover .module-icon {
        transform: rotate(360deg) scale(1.1);
      }

      .stats-card {
        background: linear-gradient(135deg, ${colors.cardBgBlack} 0%, rgba(229, 118, 39, 0.05) 100%);
        border: 1px solid rgba(229, 118, 39, 0.2);
        border-radius: 15px;
        padding: 2rem;
        text-align: center;
        transition: all 0.4s ease;
        height: 100%;
      }

      .stats-card:hover {
        transform: translateY(-5px);
        border-color: var(--color-primary);
        box-shadow: 0 15px 30px rgba(229, 118, 39, 0.2);
      }

      .stats-icon {
        width: 60px;
        height: 60px;
        background: linear-gradient(135deg, var(--color-primary) 0%, ${colors.primaryHover} 100%);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 1rem;
        transition: all 0.3s ease;
      }

      .stats-card:hover .stats-icon {
        transform: rotate(360deg) scale(1.1);
      }

      .back-button {
        background: rgba(229, 118, 39, 0.1);
        border: 2px solid var(--color-primary);
        color: var(--color-primary);
        padding: 12px 24px;
        border-radius: 25px;
        display: flex;
        align-items: center;
        gap: 8px;
        transition: all 0.3s ease;
        text-decoration: none;
        font-weight: 500;
      }

      .back-button:hover {
        background: var(--color-primary);
        color: ${colors.white};
        transform: translateX(-5px);
      }

      .cta-button {
        background: linear-gradient(135deg, var(--color-primary) 0%, ${colors.primaryHover} 100%);
        border: none;
        color: ${colors.white};
        padding: 15px 30px;
        border-radius: 30px;
        font-size: 1.1rem;
        font-weight: 600;
        transition: all 0.3s ease;
        box-shadow: 0 8px 20px rgba(229, 118, 39, 0.3);
        cursor: pointer;
      }

      .cta-button:hover {
        transform: translateY(-3px);
        box-shadow: 0 15px 35px rgba(229, 118, 39, 0.4);
      }

      .implementation-step {
        background: linear-gradient(135deg, ${colors.cardBgBlack} 0%, rgba(229, 118, 39, 0.05) 100%);
        border: 1px solid rgba(229, 118, 39, 0.2);
        border-radius: 15px;
        padding: 1.5rem;
        text-align: center;
        position: relative;
        transition: all 0.4s ease;
        margin-bottom: 2rem;
      }

      .implementation-step:hover {
        transform: translateY(-5px);
        border-color: var(--color-primary);
        box-shadow: 0 15px 30px rgba(229, 118, 39, 0.2);
      }

      .implementation-step::after {
        content: '';
        position: absolute;
        bottom: -20px;
        left: 50%;
        transform: translateX(-50%);
        width: 2px;
        height: 40px;
        background: linear-gradient(to bottom, var(--color-primary), transparent);
      }

      .implementation-step:last-child::after {
        display: none;
      }

      .step-number {
        width: 40px;
        height: 40px;
        background: linear-gradient(135deg, var(--color-primary) 0%, ${colors.primaryHover} 100%);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 1rem;
        font-weight: bold;
        color: ${colors.white};
        font-size: 1.2rem;
      }

      .fade-in {
        animation: fadeIn 0.8s ease-in-out;
      }

      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(30px); }
        to { opacity: 1; transform: translateY(0); }
      }

      .tab-content {
        animation: slideIn 0.5s ease-in-out;
      }

      @keyframes slideIn {
        from { opacity: 0; transform: translateX(20px); }
        to { opacity: 1; transform: translateX(0); }
      }

      /* Mobile-specific styles */
      @media (max-width: 768px) {
        .mobile-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        //   margin-bottom: 1rem;
          width: 100%;
        }
        
        .back-button-mobile {
          background: rgba(229, 118, 39, 0.1);
          border: 2px solid var(--color-primary);
          color: var(--color-primary);
          padding: 8px 16px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.9rem;
          font-weight: 500;
          margin: 0;
        }
        
        .category-badge-mobile {
          background: linear-gradient(135deg, var(--color-primary)20 0%, ${colors.primaryHover}20 100%);
          color: var(--color-primary);
          padding: 6px 12px;
          border-radius: 16px;
          font-size: 0.8rem;
          font-weight: '600';
          border: 1px solid var(--color-primary)40;
          white-space: nowrap;
        }
        
        .hero-image img {
          height: 250px;
        }
        
        .product-title-mobile {
          font-size: 2rem !important;
          margin-bottom: 1rem !important;
        }
        
        .product-description-mobile {
          font-size: 1rem !important;
          margin-bottom: 1.5rem !important;
        }
        
        .cta-button-mobile {
          padding: 12px 24px !important;
          font-size: 1rem !important;
          margin-bottom: 1.5rem !important;
          width: 100%;
          text-align: center;
        }
        
        .product-tab {
          font-size: 0.9rem;
          padding: 10px 18px;
          margin: 0 4px 8px 0;
        }
        
        .modal-content-custom {
          width: 95%;
        }
        
        .module-card, .feature-card, .stats-card {
          padding: 1rem;
        }
        
        .module-icon, .stats-icon {
          width: 50px;
          height: 50px;
        }
      }

      @media (max-width: 480px) {
        .product-title-mobile {
          font-size: 1.75rem !important;
          margin-top : -30px;
        }
          .mobile-header-row{
          margin-bottom: -20px;
          
          }
          
        
        .product-description-mobile {
          font-size: 0.95rem !important;
        }
        
        .back-button-mobile {
          padding: 6px 12px;
          font-size: 0.85rem;
        }
        
        .category-badge-mobile {
          padding: 5px 10px;
          font-size: 1.0rem;
        }
        
        .cta-button-mobile {
          padding: 10px 20px !important;
          font-size: 0.95rem !important;
        }
      }
    `;

    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  if (!productData) {
    return (
      <div
        style={{
          backgroundColor: colors.bodyBlack,
          minHeight: "100vh",
          cursor: "pointer",
        }}
        className="d-flex align-items-center justify-content-center"
      >
        <div style={{ color: colors.white, textAlign: "center" }}>
          <h3>Product not found</h3>
          <button onClick={() => navigate(-1)} className="btn btn-primary mt-3">
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <div className="tab-content">
            <div className="row">
              <div className="col-lg-12">
                <h4 style={{ color: colors.white, marginBottom: "1.5rem" }}>
                  Product Overview
                </h4>
                <p
                  style={{
                    color: colors.whiteTransparent,
                    lineHeight: "1.8",
                    fontSize: "1.1rem",
                  }}
                >
                  {productData.fullDescription}
                </p>

                <h5
                  style={{
                    color: `var(--color-primary)`,
                    marginTop: "2rem",
                    marginBottom: "1rem",
                  }}
                >
                  Key Benefits
                </h5>
                <div className="row">
                  {productData.benefits?.map((benefit, index) => (
                    <div key={index} className="col-md-6 mb-3">
                      <div className="feature-card">
                        <div className="d-flex align-items-center">
                          <CheckCircle
                            style={{
                              color: `var(--color-primary)`,
                              marginRight: "12px",
                            }}
                            size={20}
                          />
                          <span style={{ color: colors.white }}>{benefit}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="row mt-4">
                  <div className="col-md-6">
                    <div className="stats-card">
                      <div className="stats-icon">
                        <Users color={colors.white} size={24} />
                      </div>
                      <h6
                        style={{ color: colors.white, marginBottom: "0.5rem" }}
                      >
                        Support
                      </h6>
                      <p
                        style={{
                          color: `var(--color-primary)`,
                          fontSize: "1.1rem",
                          fontWeight: "600",
                          marginBottom: 0,
                        }}
                      >
                        {productData.support || "24/7 Support"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case "features":
        return (
          <div className="tab-content">
            <h4 style={{ color: colors.white, marginBottom: "1.5rem" }}>
              Key Features & Capabilities
            </h4>
            <div className="row">
              {productData.keyFeatures?.map((feature, index) => (
                <div key={index} className="col-lg-6 mb-3">
                  <div className="feature-card">
                    <div className="d-flex align-items-start">
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          background: `var(--color-primary)`,
                          borderRadius: "50%",
                          marginTop: "8px",
                          marginRight: "15px",
                          flexShrink: 0,
                        }}
                      ></div>
                      <div>
                        <h6
                          style={{
                            color: colors.white,
                            marginBottom: "0.5rem",
                          }}
                        >
                          {feature}
                        </h6>
                        {/* <p style={{ color: colors.whiteTransparent, fontSize: '0.9rem', marginBottom: 0 }}>
                          Advanced {feature.toLowerCase()} functionality with enterprise-grade security and performance.
                        </p> */}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "modules":
        return (
          <div className="tab-content">
            <h4 style={{ color: colors.white, marginBottom: "1.5rem" }}>
              Product Modules
            </h4>
            <div className="row">
              {productData.modules?.map((module, index) => {
                const icons = [Package, Layers, Settings, BarChart3];
                const IconComponent = icons[index % icons.length];

                return (
                  <div key={index} className="col-lg-6 col-md-6 mb-4">
                    <div className="module-card">
                      <div className="module-icon">
                        <IconComponent color={colors.white} size={24} />
                      </div>
                      <h5 style={{ color: colors.white, marginBottom: "1rem" }}>
                        {module.name}
                      </h5>
                      <p
                        style={{
                          color: colors.whiteTransparent,
                          marginBottom: 0,
                        }}
                      >
                        {module.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "technologies":
        return (
          <div className="tab-content">
            <h4 style={{ color: colors.white, marginBottom: "1.5rem" }}>
              Technologies & Tools
            </h4>
            <div className="mb-4">
              {productData.technologies?.map((tech, index) => (
                <span key={index} className="tech-badge">
                  {tech}
                </span>
              ))}
            </div>

            <div className="row mt-4">
              <div className="col-lg-8">
                <h5
                  style={{
                    color: `var(--color-primary)`,
                    marginBottom: "1rem",
                  }}
                >
                  Technology Stack Benefits
                </h5>
                <div className="feature-card">
                  <p
                    style={{
                      color: colors.whiteTransparent,
                      lineHeight: "1.6",
                    }}
                  >
                    Our technology stack is carefully selected to ensure maximum
                    performance, security, and scalability. We use
                    industry-leading tools and frameworks that provide robust
                    functionality while maintaining flexibility for future
                    enhancements and integrations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case "implementation":
        return (
          <div className="tab-content">
            <h4 style={{ color: colors.white, marginBottom: "1.5rem" }}>
              Implementation Process
            </h4>
            <div className="row">
              <div className="col-lg-12">
                {[
                  {
                    step: 1,
                    title: "Requirements Analysis",
                    description:
                      "We conduct thorough analysis of your business requirements and current systems to design the optimal solution.",
                  },
                  {
                    step: 2,
                    title: "System Design & Planning",
                    description:
                      "Our team creates detailed system architecture and implementation roadmap with clear milestones.",
                  },
                  {
                    step: 3,
                    title: "Development & Configuration",
                    description:
                      "We develop and configure the system according to your specifications using best practices.",
                  },
                  {
                    step: 4,
                    title: "Testing & Quality Assurance",
                    description:
                      "Comprehensive testing ensures the system meets all requirements and performs optimally.",
                  },
                  {
                    step: 5,
                    title: "Deployment & Training",
                    description:
                      "We deploy the system and provide comprehensive training to ensure successful adoption.",
                  },
                  {
                    step: 6,
                    title: "Go-Live & Support",
                    description:
                      "Full system launch with ongoing support and maintenance to ensure continued success.",
                  },
                ].map((process, index) => (
                  <div
                    key={index}
                    className="implementation-step fade-in"
                    style={{ animationDelay: `${index * 0.2}s` }}
                  >
                    <div className="step-number">{process.step}</div>
                    <h6
                      style={{
                        color: `var(--color-primary)`,
                        marginBottom: "0.8rem",
                      }}
                    >
                      {process.title}
                    </h6>
                    <p
                      style={{
                        color: colors.whiteTransparent,
                        marginBottom: 0,
                      }}
                    >
                      {process.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ backgroundColor: colors.bodyBlack, minHeight: "100vh" }}>
      <SEO
        title={
          productData?.title
            ? `${productData.title} | Product Details | IDLBAY`
            : "Product Details | IDLBAY"
        }
        description={
          productData?.description ||
          "Explore detailed information about this product offered by IDLBAY."
        }
        keywords={`IDLBAY, product details, ${
          productData?.title || ""
        }, software, digital products`}
        url={`https://idlbay.com/productdetails/${productData?.id || ""}`}
        organization={organization}
      />
      {/* Header Section */}
      <div
        className="product-details-header py-3"
        style={{ position: "relative", zIndex: 1 }}
      >
        <div className="container" style={{ position: "relative", zIndex: 2 }}>
          {/* Mobile Header Row */}
          {isMobile && (
            <div className="mobile-header-row">
              <button
                onClick={() => navigate(-1)}
                className="back-button-mobile"
                style={{
                  border: "none",
                  background: "transparent",
                  color: `var(--color-primary)`,
                  cursor: "pointer",
                }}
              >
                <ArrowLeft size={18} />
              </button>

              <span className="category-badge-mobile">
                {productData.category}
              </span>
            </div>
          )}

          <div className="row align-items-center py-5">
            <div
              className="col-lg-6"
              style={{
                position: "relative",
                zIndex: 2,
                paddingBottom: "20px",
                paddingTop: "30px",
              }}
            >
              {/* Desktop Category Badge */}
              {!isMobile && (
                <div className="mb-3">
                  <button
                    onClick={() => navigate(-1)}
                    className="back-button-mobile"
                    style={{
                      border: "none",
                      background: "transparent",
                      color: `var(--color-primary)`,
                      cursor: "pointer",
                    }}
                  >
                    <ArrowLeft size={18} />
                  </button>
                  <span
                    style={{
                      background: `linear-gradient(135deg, var(--color-primary)20 0%, ${colors.primaryHover}20 100%)`,
                      color: `var(--color-primary)`,
                      padding: "0.5rem 1rem",
                      borderRadius: "20px",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      border: `1px solid var(--color-primary)40`,
                    }}
                  >
                    {productData.category}
                  </span>
                </div>
              )}

              <h1
                style={{
                  color: colors.white,
                  fontSize: isMobile ? "2rem" : "3rem",
                  fontWeight: "700",
                  marginBottom: isMobile ? "1rem" : "1.5rem",
                  lineHeight: "1.2",
                }}
                className={`fade-in ${isMobile ? "product-title-mobile" : ""}`}
              >
                {productData.title}
              </h1>

              <h2
                style={{
                  color: colors.whiteTransparent,
                  fontSize: isMobile ? "1rem" : "1.2rem",
                  lineHeight: "1.8",
                  marginBottom: isMobile ? "1.5rem" : "2rem",
                }}
                className={`fade-in ${
                  isMobile ? "product-description-mobile" : ""
                }`}
              >
                {productData.description}
              </h2>

              <button
                onClick={handleContactClick}
                className={`cta-button fade-in ${
                  isMobile ? "cta-button-mobile" : ""
                }`}
                style={{ position: "relative", zIndex: 3 }}
              >
                Get Started Now
              </button>
            </div>

            <div
              className="col-lg-6"
              style={{ position: "relative", zIndex: 2 }}
            >
              <div className="hero-image fade-in">
                <img
                  src={
                    productData.image
                      ? productData.image.startsWith("http")
                        ? productData.image
                        : `https://api.dremerz.net/uploads/${productData.image}`
                      : "https://via.placeholder.com/600x400/333/fff?text=Product+Image"
                  }
                  alt={productData.title}
                  onError={(e) => {
                    e.target.src =
                      "https://via.placeholder.com/600x400/333/fff?text=Image+Not+Found";
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="container py-5">
        <div className="row">
          <div className="col-12">
            <div className="d-flex flex-wrap justify-content-center mb-4">
              {[
                { key: "overview", label: "Overview" },
                { key: "features", label: "Features" },
                ...(productData.modules
                  ? [{ key: "modules", label: "Modules" }]
                  : []),
                // { key: 'implementation', label: 'Implementation' }
              ].map((tab) => (
                <button
                  key={tab.key}
                  className={`product-tab ${
                    activeTab === tab.key ? "active" : ""
                  }`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">{renderTabContent()}</div>
        </div>
      </div>

      {/* Get In Touch Modal */}
      {showGetInTouch && (
        <div className="modal-overlay" onClick={closeGetInTouch}>
          <div
            className="modal-content-custom"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-button" onClick={closeGetInTouch}>
              &times;
            </button>
            <GetInTouchForm isModal={true} />
          </div>
        </div>
      )}

      <Readytostart />
    </div>
  );
};

export default ProductFullDetailsView;
